import { useEffect, useRef, useState } from 'react';
import {
  useGetProjectPdfsQuery,
  useCreatePdfMutation,
  useUploadPdfFileMutation,
  useRenamePdfMutation,
  useDeletePdfMutation,
} from 'app/services/projectBoardApi';
import type { IProjectPdf } from 'models';
import { useTabAccess } from '../../hooks/useProjectBoardData';
import {
  FilesNotesDeletePopup,
  RowActionsDropdown,
  extractPdfErrorMessage,
  pdfSizeLimitError,
  PDF_SIZE_LIMIT_MB,
  scheduleTitleFocus,
  caretToEnd,
} from './filesNotesShared';

/**
 * FILES TAB (`#files` — `files/view/files.js` + files.tmpl), inventory §B4 /
 * parity 168–175.
 *
 * Boot: `GET /project/pdf/?project_id={id}`; the chassis renders only after
 * the fetch (the Backbone mount stays empty until `.then(render)`), then the
 * FIRST file tab auto-opens — and re-opens on EVERY re-entry to the tab
 * (changePrjAppViewState's `.files-block >li:nth-child(1) a.files-fields`
 * synthetic click — parity 173; the synthetic click does NOT fire the
 * mousedown bookkeeping, so re-entry selects without the 1s title focus).
 *
 * + ADD pipeline (parity 169): picking a PDF POSTs the record (`file_name
 * 'Project file'`) then multipart-PATCHes `pdf_file` (raw-XHR parity in the
 * uploadPdfFile mutation); success adds the row, selects its tab, focuses the
 * title (1s) and shows the PDF; failure shows the server error text in
 * `#myTabContent` and ROLLS BACK the just-created record via DELETE. (The
 * legacy first refetches the collection purely to materialize the model for
 * `destroy()` — that bookkeeping GET is skipped here; no user-visible delta.)
 *
 * PERMISSION GATING (parity 174/175 / OQ B-4, settled like 3e): the UI gates
 * by the **files** tab access flag — the legacy's INTENDED disable block
 * (`input[disabled]` + `button.non-clickable`) runs against the WRONG (previous)
 * DOM and so never works [NEG the broken ordering — working read-only here];
 * the API itself authorizes by the **'project'** tab flag (server 403 is the
 * real guard — replicated awareness, not the bug).
 *
 * [NEG]s honored: shadowed first `addFiles` (duplicate events-hash key — only
 * the POST+PATCH pipeline is live), "Reset Use Defined Field?" icon-click
 * popup mode + handler-less Reset button, dead `saveFileFieldData`/`cancel`
 * bindings, `GET /project/limit/` never called, the legacy empty-hash
 * `location.href += 'files'` rewrite (React routing normalizes the hash before
 * any tab mounts), and no realtime sync (Files models have no classID).
 */

/** Iframe viewer with the legacy fadeOut(50)/attr(src)/fadeIn(150) swap. */
const PdfFrame = ({ id, src }: { id: number; src: string }) => {
  const [shownSrc, setShownSrc] = useState(src);
  const [faded, setFaded] = useState(false);
  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return undefined;
    }
    if (src === shownSrc) return undefined;
    setFaded(true); // fadeOut 50ms
    const t = setTimeout(() => {
      setShownSrc(src);
      setFaded(false); // fadeIn 150ms
    }, 50);
    return () => clearTimeout(t);
  }, [src, shownSrc]);

  return (
    <iframe
      // Template quirk kept: the pdf_file URL doubles as a class name
      // (`class="{{pdf_file}} iframe-css"`).
      className={`${shownSrc} iframe-css`}
      id={`view_pdf_${id}`}
      title={`view_pdf_${id}`}
      style={{
        width: '100%',
        height: '100%',
        opacity: faded ? 0 : 1,
        transition: faded ? 'opacity 50ms' : 'opacity 150ms',
      }}
      frameBorder={0}
      // DEFENSIVE DELTA: an empty legacy src ("") makes the iframe load the
      // page itself recursively — omit the attribute instead (blank pane).
      src={shownSrc || undefined}
    />
  );
};

/**
 * Right-pane title input — rename saves on `change` (blur/Enter with a
 * modified value) with NO client length guard (unlike Notes — parity 171);
 * the inline onfocus caret-to-end hack is kept.
 */
const FileTitleInput = ({
  file,
  access,
  onCommit,
}: {
  file: IProjectPdf;
  access: boolean;
  onCommit: (value: string) => void;
}) => {
  const synced = file.file_name ?? '';
  const [draft, setDraft] = useState(synced);
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (document.activeElement !== ref.current) setDraft(synced);
  }, [synced]);

  const commit = () => {
    if (draft !== synced) onCommit(draft);
  };

  return (
    <input
      ref={ref}
      onFocus={(e) => caretToEnd(e.target)}
      type="text"
      id={`title_files_field_${file.id}`}
      name="file_name"
      className="col-xs- textarea-field-title files_field title_files_field"
      minLength={3}
      maxLength={65}
      value={draft}
      data-id={file.id}
      disabled={!access}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
      }}
    />
  );
};

const FilesTab = ({ projectId, active }: { projectId: number; active: boolean }) => {
  const { data: files } = useGetProjectPdfsQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const access = useTabAccess(projectId, 'files');
  const [createPdf] = useCreatePdfMutation();
  const [uploadPdf] = useUploadPdfFileMutation();
  const [renamePdf] = useRenamePdfMutation();
  const [deletePdf] = useDeletePdfMutation();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [deleteFor, setDeleteFor] = useState<IProjectPdf | null>(null);
  /** #pdf_loader preloader (shown during either upload PATCH). */
  const [uploading, setUploading] = useState(false);
  /** + ADD failure span at the #myTabContent level (errorMSG no-block path). */
  const [addError, setAddError] = useState<string | null>(null);
  /** Per-pane upload failure — replaces the iframe with #span_error. */
  const [paneErrors, setPaneErrors] = useState<Record<number, string>>({});

  // First-tab auto-open on load and on every tab re-entry (parity 173).
  const prevActive = useRef(false);
  const bootSelected = useRef(false);
  useEffect(() => {
    if (!files) {
      prevActive.current = active;
      return;
    }
    const entered = active && !prevActive.current;
    if ((active && !bootSelected.current) || entered) {
      bootSelected.current = true;
      setSelectedId(files.length ? files[0].id : null);
    }
    prevActive.current = active;
  }, [active, files]);

  // ---- + ADD: POST create → multipart PATCH → rollback DELETE on error ----
  const onAddPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    input.value = ''; // the legacy form is rebuilt on re-render — same re-pick affordance
    if (!file || !access) return;
    setAddError(null);
    // Oversized: show the cap and stop here. The legacy end state on an upload
    // failure is the same — the message in #myTabContent and no row, since the
    // just-created record is rolled back — so the record is never created.
    const tooBig = pdfSizeLimitError(file);
    if (tooBig) {
      setAddError(tooBig);
      return;
    }
    createPdf({ project_id: projectId, file_name: 'Project file' })
      .unwrap()
      .then((created) => {
        setUploading(true);
        uploadPdf({ id: created.id, projectId, file })
          .unwrap()
          .then((row) => {
            setUploading(false);
            // addViewPDF: select the new tab + 1s title focus; iframe renders
            // from the echoed pdf_file URL (localPdfURl equivalent).
            setSelectedId(row.id);
            scheduleTitleFocus(`title_files_field_${row.id}`);
          })
          .catch((err) => {
            setUploading(false);
            setAddError(extractPdfErrorMessage(err));
            // errorMSG rollback: destroy the just-created record (parity 169).
            deletePdf({ id: created.id, projectId });
          });
      })
      .catch(() => undefined); // failed POST: legacy chain simply stops
  };

  // ---- per-file Upload: multipart PATCH, iframe hot-swap / error span ----
  const onUploadPick = (id: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !access) return;
    // Same pre-flight gate as + ADD: the per-pane error span, no PATCH.
    const tooBig = pdfSizeLimitError(file);
    if (tooBig) {
      setPaneErrors((prev) => ({ ...prev, [id]: tooBig }));
      return;
    }
    setUploading(true);
    uploadPdf({ id, projectId, file })
      .unwrap()
      .then(() => {
        setUploading(false);
        // viewPDF: a present error span is replaced by the fresh iframe.
        setPaneErrors((prev) => {
          if (!(id in prev)) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
      })
      .catch((err) => {
        setUploading(false);
        setPaneErrors((prev) => ({ ...prev, [id]: extractPdfErrorMessage(err) }));
      });
  };

  // updateFileField: save {file_name} (patch); the active tab stays active
  // (active_last_change — selection state already persists across the echo).
  const onRename = (id: number, value: string) => {
    renamePdf({ id, projectId, file_name: value });
  };

  // removeFileField: optimistic destroy + re-render WITHOUT the first-tab
  // click — after a delete NO tab is selected (parity 172, Files-only quirk).
  const onConfirmDelete = () => {
    if (!deleteFor) return;
    deletePdf({ id: deleteFor.id, projectId });
    setDeleteFor(null);
    setSelectedId(null);
  };

  if (!files) return null; // Backbone renders only after the collection fetch

  return (
    <div id="files_format" className=" tab-content files-tab__content content">
      <div id="files" className="col-xs-12 full-height ">
        <div className=" full-height">
          <div className="row full-height">
            <div className="col-xs-3 full-height left-column files-content-files-format">
              <div className="row block-header">
                <div className="col-xs-12 padding-right-0">
                  <div className="row">
                    <div className="col-xs-1 account-icon padding-left-0">
                      {' '}
                      <i className="icon-files" />
                    </div>
                    <div className="col-xs-7 block-header-title padding-left-0 padding-top-14">
                      <span className="block-title ">Files</span>
                    </div>
                    <div className="col-xs-4 block-header-button">
                      <form id="pdf_upload" encType="multipart/form-data" data-id="">
                        <label className="editing-btn udf_pdf_field">
                          <input
                            id="pdf_file_add"
                            type="file"
                            data-id=""
                            name="pdf_file"
                            className="hide"
                            tabIndex={-1}
                            accept="application/pdf"
                            disabled={!access}
                            onChange={onAddPick}
                          />
                          <span className="pdf-file-label btn-add btn add pdf-file-label" id="">
                            + ADD
                          </span>
                        </label>
                      </form>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row files-list">
                <div className="col-xs-12 full-height">
                  <div className="files-scroll row">
                    <ul
                      className="nav nav-tabs files-block row margin-right-0"
                      id="myTab"
                      role="tablist"
                    >
                      {files.map((f) => (
                        <li
                          key={f.id}
                          className={`nav-item files-tab col-xs-12${
                            selectedId === f.id ? ' active' : ''
                          }`}
                          id={`file_field_${f.id}`}
                          data-id={f.id}
                        >
                          <div className=" margin-right-0">
                            <div className="col-xs-12">
                              <a
                                className="files-fields files-fields-files"
                                id={`pdf_file_field_${f.id}-tab`}
                                data-toggle="tab"
                                href={`#pdf_file_field_${f.id}`}
                                role="tab"
                                aria-controls={`pdf_file_field_${f.id}-tab`}
                                aria-selected="true"
                                data-id={f.id}
                                onMouseDown={() =>
                                  // changeFilesFild: activeID bookkeeping + 1s
                                  // title focus (fires on every real click,
                                  // including the already-active tab).
                                  scheduleTitleFocus(`title_files_field_${f.id}`)
                                }
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSelectedId(f.id);
                                }}
                              >
                                {f.file_name}
                              </a>
                              <RowActionsDropdown
                                id={f.id}
                                dropdownClassName="dropdown_files_fields"
                                topOffset={30}
                                leftOffset={-110}
                                disabled={!access}
                              >
                                {(close) => (
                                  <li>
                                    <a
                                      tabIndex={-1}
                                      href="#"
                                      data-id={f.id}
                                      className="remove"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        close();
                                        setDeleteFor(f);
                                      }}
                                    >
                                      <i className="icon-trash" />
                                      Delete
                                    </a>
                                  </li>
                                )}
                              </RowActionsDropdown>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="file-notes note">
                    {/* The number the legacy template hardcoded here is now the
                        one actually enforced, from the same constant. */}
                    <div>PDF format, {PDF_SIZE_LIMIT_MB} MB max size</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xs-9 full-height righ-column   content-item">
              <div className="row row-content-item  full-height">
                <div className="col-xs-12  tab-content full-height" id="myTabContent">
                  {files.map((f) => (
                    <div
                      key={f.id}
                      className={`full-height row tab-pane fade pdf_file_field_${f.id}${
                        selectedId === f.id ? ' active in' : ''
                      }`}
                      id={`pdf_file_field_${f.id}`}
                      role="tabpanel"
                      aria-labelledby={`pdf_file_field_${f.id}-tab`}
                    >
                      <div className="col-xs-12 content-header">
                        <div className="col-xs-6 content-title">
                          <FileTitleInput
                            file={f}
                            access={access}
                            onCommit={(value) => onRename(f.id, value)}
                          />
                        </div>
                        <div className="col-xs-6">
                          <div className="row">
                            <div className="col-xs-7" />
                            <div className="col-xs-5">
                              <div className="row upload-dounload-block">
                                <div className="col-xs-12 text-right">
                                  <form
                                    id={`pdf_upload_${f.id}`}
                                    encType="multipart/form-data"
                                    data-id={f.id}
                                  >
                                    <label className="editing-btn udf_pdf_field">
                                      <i className="icon-upload-file-black-24dp udf-type-icon" />
                                      <input
                                        id="pdf_file"
                                        type="file"
                                        data-id={f.id}
                                        name="pdf_file"
                                        className="hide"
                                        tabIndex={-1}
                                        accept="application/pdf"
                                        disabled={!access}
                                        onChange={(e) => onUploadPick(f.id, e)}
                                      />
                                      <span className="pdf-file-label" id={String(f.id)}>
                                        Upload
                                      </span>
                                    </label>
                                  </form>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-12 content-tooltip">
                        <div className="col-xs-6 title-tooltip">
                          <span>3 - 65 characters</span>
                        </div>
                      </div>
                      <div className="col-xs-12 content-files full-height">
                        <div id={`pdf_block_${f.id}`} className="pdf-block full-height">
                          {paneErrors[f.id] != null ? (
                            <span id="span_error"> {paneErrors[f.id]}</span>
                          ) : (
                            <PdfFrame id={f.id} src={(f.pdf_file as string | null) ?? ''} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {uploading && (
                    <div
                      id="pdf_loader"
                      className="col-xs-12 dotted-preloader active 222"
                      style={{ position: 'absolute' }}
                    >
                      {' '}
                    </div>
                  )}
                  {addError != null && <span id="span_error"> {addError}</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {deleteFor && (
        <FilesNotesDeletePopup
          title="Delete File?"
          content="If you choose to Delete, all File Data will be lost and will not be recoverable ."
          name={deleteFor.file_name}
          onConfirm={onConfirmDelete}
          onClose={() => setDeleteFor(null)}
        />
      )}
    </div>
  );
};

export default FilesTab;
